import { OpenAPI } from '@api'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { isCUI } from '@/consts/routes'
import { getPersistedToken } from '@/modules/accounts/utils/token'
import {
  handleOFLogout,
  isOFLogoutError,
} from '@/modules/chatting/utils/OFLogout'

import {
  BACKEND_SERVER,
  BACKEND_URL,
  BACKEND_URL_V2,
  BACKEND_URL_V3,
} from './constants'

const axiosRequestConfig: AxiosRequestConfig = {
  baseURL: BACKEND_URL,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
}

const axiosRequestConfigV2: AxiosRequestConfig = {
  baseURL: BACKEND_URL_V2,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
}

const axiosRequestConfigV3: AxiosRequestConfig = {
  baseURL: BACKEND_URL_V3,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
}

const axiosRequestConfigChatting: AxiosRequestConfig = {
  baseURL: BACKEND_URL,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
}

const _requestInterceptor = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const token = getPersistedToken(LocalStorageKeys.AUTH_TOKEN)

  if (config.headers.Authorization === undefined && token) {
    config.headers.set(
      'Authorization',
      token.startsWith('Bearer ') ? token : `Bearer ${token}`,
    )
  }

  return config
}

const _responseInterceptor = (error: unknown) => {
  if (isCUI && isOFLogoutError(error)) {
    handleOFLogout()
  }

  return Promise.reject(error)
}

OpenAPI.BASE = BACKEND_SERVER
OpenAPI.TOKEN = getPersistedToken(LocalStorageKeys.AUTH_TOKEN) ?? undefined

// add default interceptors
axios.interceptors.request.use(_requestInterceptor)
axios.interceptors.response.use(
  (response) => response,
  (error) => _responseInterceptor(error),
)

const apiClient: AxiosInstance = axios.create(axiosRequestConfig)

apiClient.interceptors.request.use(_requestInterceptor)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => _responseInterceptor(error),
)

const apiClientV2: AxiosInstance = axios.create(axiosRequestConfigV2)

apiClientV2.interceptors.request.use(_requestInterceptor)
apiClientV2.interceptors.response.use(
  (response) => response,
  (error) => _responseInterceptor(error),
)

const apiClientV3: AxiosInstance = axios.create(axiosRequestConfigV3)

apiClientV3.interceptors.request.use(_requestInterceptor)
apiClientV3.interceptors.response.use(
  (response) => response,
  (error) => _responseInterceptor(error),
)

const apiClientChatting: AxiosInstance = axios.create(
  axiosRequestConfigChatting,
)

apiClientChatting.interceptors.request.use(_requestInterceptor)
apiClientChatting.interceptors.response.use(
  (response) => response,
  (error) => _responseInterceptor(error),
)

export { apiClient, apiClientV2, apiClientV3, apiClientChatting }
